import { csrfToken } from "./csrfTokenUtils";

// Resolves once the user has re-authenticated after a session timeout.
const waitForSessionRestore = () =>
    new Promise((resolve) => {
        const handler = () => {
            window.removeEventListener("app:session-restored", handler);
            resolve();
        };
        window.addEventListener("app:session-restored", handler);
    });

const handleUnauthorized = (response) => {
    if (response.status === 401) {
        if (typeof window !== "undefined" && window.__REQUIRES_AUTH__ && !window.__SESSION_TIMEOUT_ACTIVE__) {
            window.__SESSION_TIMEOUT_ACTIVE__ = true;
            window.dispatchEvent(new CustomEvent("app:session-expired"));
        }
        return true;
    }
    return false;
};

// Global request deduplication for customAPIRequest - prevents duplicate calls
const pendingCustomRequests = new Map();

const customAPIRequest = async (url, formMethod, data) => {
    const normalizedUrl = `/${url}`;
    const upperMethod = (formMethod || "POST").toUpperCase();
    
    // Create a unique key from URL + method + payload
    // This allows deduplication of identical requests while allowing different payloads
    const payloadForKey = { ...(data || {}) };
    // Sort keys for consistent serialization
    const sortedPayload = Object.keys(payloadForKey)
        .sort()
        .reduce((acc, key) => {
            acc[key] = payloadForKey[key];
            return acc;
        }, {});
    const payloadString = JSON.stringify(sortedPayload);
    const requestKey = `${normalizedUrl}::${upperMethod}::${payloadString}`;

    // CRITICAL: Check if there's already a pending request with the same key
    if (pendingCustomRequests.has(requestKey)) {
        return pendingCustomRequests.get(requestKey);
    }
    
    // Create the request promise
    const requestPromise = new Promise(async (resolve, reject) => {
        try {
            const normalizedData = {...data, 'authenticity_token':csrfToken()};
            
            const myHeaders = new Headers();
            myHeaders.append("Cookie", csrfToken());
            myHeaders.append("Content-Type", "application/json");
            
            let fetchData = await fetch(normalizedUrl, {
                method: formMethod,
                headers: myHeaders,
                body: JSON.stringify(normalizedData)
            });

            if (handleUnauthorized(fetchData)) {
                // Hold the promise open until the user re-logs-in, then retry.
                await waitForSessionRestore();
                const retryData = {...data, 'authenticity_token': csrfToken()};
                const retryHeaders = new Headers();
                retryHeaders.append("Cookie", csrfToken());
                retryHeaders.append("Content-Type", "application/json");
                const retryFetch = await fetch(normalizedUrl, {
                    method: formMethod,
                    headers: retryHeaders,
                    body: JSON.stringify(retryData)
                });
                const retryJson = await retryFetch.json();
                resolve({'data': retryJson});
                return;
            }
            let fetchDataJSON = await fetchData.json();
            resolve({'data': fetchDataJSON});
        } catch(error) {
            reject({'error': error});
        } finally {
            // Remove from pending requests AFTER completion
            pendingCustomRequests.delete(requestKey);
        }
    });
    
    // Store the promise IMMEDIATELY (synchronously) before any async operations
    pendingCustomRequests.set(requestKey, requestPromise);
    
    return requestPromise;
}
export default customAPIRequest;